/**
 * VaultDetailsModal - Detailed vault information modal
 * Shows collateral balances, deposit/withdraw history, health metrics
 */

import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ArrowTrendingUpIcon, ClockIcon, ShieldCheckIcon } from '../../assets/icons'
import { useAccount } from 'wagmi'
import { Card, CardContent } from '../common/Card'
import { Button } from '../common/Button'
import { Badge } from '../common/Badge'
import { formatTokenAmount, formatAddress } from '../../utils/format'
import { useAccountCollateralBalance } from '../../hooks/useVault'
import { useTransactionHistory } from '../../hooks/useTransactionHistory'
import { useTokenPrices } from '../../hooks/useTokenPrice'
import { useUserLOCsFromEvents } from '../../hooks/useLOCEvents'
import { SUPPORTED_TOKENS } from '@contracts/tokens'
import { formatDistanceToNow } from 'date-fns'

interface VaultDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function VaultDetailsModal({ isOpen, onClose }: VaultDetailsModalProps) {
  const { address } = useAccount()
  
  // Fetch all collateral balances
  const depositableTokens = Object.values(SUPPORTED_TOKENS).filter(t => !t.isNative)
  
  const tokenBalances = depositableTokens.map(token => {
    const { availableBalance, reservedBalance } = useAccountCollateralBalance(
      address,
      token.address
    )
    const total = (availableBalance || 0n) + (reservedBalance || 0n)
    return {
      token,
      availableBalance: availableBalance || 0n,
      reservedBalance: reservedBalance || 0n,
      totalBalance: total,
    }
  }).filter(b => b.totalBalance > 0n)
  
  // Fetch transaction history
  const { transactions, isLoading: isLoadingHistory } = useTransactionHistory(address)
  
  // Fetch token prices
  const { data: prices } = useTokenPrices(depositableTokens)
  
  // Fetch LOCs to calculate health metrics
  const { locs } = useUserLOCsFromEvents(address)
  
  // Calculate total collateral value in USD
  const totalCollateralUSD = tokenBalances.reduce((sum, { token, totalBalance }) => {
    const price = prices?.[token.symbol] || 0
    const amount = parseFloat(formatTokenAmount(totalBalance, token.decimals))
    return sum + (amount * price)
  }, 0)
  
  // Calculate total reserved collateral value in USD
  const totalReservedUSD = tokenBalances.reduce((sum, { token, reservedBalance }) => {
    const price = prices?.[token.symbol] || 0
    const amount = parseFloat(formatTokenAmount(reservedBalance, token.decimals))
    return sum + (amount * price)
  }, 0)
  
  // Calculate vault health (available vs reserved)
  const utilizationRate = totalCollateralUSD > 0 
    ? (totalReservedUSD / totalCollateralUSD) * 100 
    : 0
  
  // Determine health status
  const getHealthStatus = (rate: number): { label: string; color: string; variant: 'success' | 'warning' | 'error' | 'info' } => {
    if (rate < 50) return { label: 'Healthy', color: 'text-green-600 dark:text-green-400', variant: 'success' }
    if (rate < 75) return { label: 'Moderate', color: 'text-yellow-600 dark:text-yellow-400', variant: 'warning' }
    if (rate < 90) return { label: 'High Use', color: 'text-orange-600 dark:text-orange-400', variant: 'warning' }
    return { label: 'Critical', color: 'text-red-600 dark:text-red-400', variant: 'error' }
  }
  
  const healthStatus = getHealthStatus(utilizationRate)
  
  // Filter transactions (last 20)
  const recentTransactions = transactions.slice(0, 20)
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6 md:p-8">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 sm:p-8 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <ShieldCheckIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white">
                        Vault Details
                      </Dialog.Title>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {address ? formatAddress(address) : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Health Metrics */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <ArrowTrendingUpIcon className="w-5 h-5" />
                      Health Metrics
                    </h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Collateral</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          ${totalCollateralUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Reserved</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          ${totalReservedUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Available</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          ${(totalCollateralUSD - totalReservedUSD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Active LOCs</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {locs?.length || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Utilization Bar */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Collateral Utilization
                        </span>
                        <Badge variant={healthStatus.variant}>
                          {healthStatus.label}
                        </Badge>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            utilizationRate < 50 ? 'bg-green-500' :
                            utilizationRate < 75 ? 'bg-yellow-500' :
                            utilizationRate < 90 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {utilizationRate.toFixed(1)}% of your collateral is reserved for active LOCs
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Collateral Balances */}
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Collateral Balances
                    </h3>
                    
                    {tokenBalances.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No collateral deposited yet
                      </p>
                    ) : (
                      <div className="overflow-x-auto -mx-6 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Asset
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Total
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Available
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Reserved
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Value (USD)
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {tokenBalances.map(({ token, totalBalance, availableBalance, reservedBalance }) => {
                                const price = prices?.[token.symbol] || 0
                                const totalAmount = parseFloat(formatTokenAmount(totalBalance, token.decimals))
                                const valueUSD = totalAmount * price
                                
                                return (
                                  <tr key={token.address} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <span className="text-2xl">{(token as any).icon}</span>
                                        <div>
                                          <p className="font-medium text-gray-900 dark:text-white">{token.symbol}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{token.name}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900 dark:text-white">
                                      {formatTokenAmount(totalBalance, token.decimals)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-green-600 dark:text-green-400 font-medium">
                                      {formatTokenAmount(availableBalance, token.decimals)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500 dark:text-gray-400">
                                      {formatTokenAmount(reservedBalance, token.decimals)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900 dark:text-white">
                                      ${valueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Transaction History */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <ClockIcon className="w-5 h-5" />
                      Recent Transactions
                    </h3>
                    
                    {isLoadingHistory ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        Loading transactions...
                      </p>
                    ) : recentTransactions.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No transactions yet
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {recentTransactions.map((tx, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                tx.type === 'deposit' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                              }`}>
                                <span className="text-lg">
                                  {tx.type === 'deposit' ? '⬇️' : '⬆️'}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 dark:text-white capitalize">
                                  {tx.type}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {tx.amount} {tx.asset} · {formatDistanceToNow(tx.timestamp, { addSuffix: true })}
                                </p>
                              </div>
                            </div>
                            {tx.txHash && (
                              <a
                                href={`https://etherscan.io/tx/${tx.txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex-shrink-0 ml-2"
                              >
                                View →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Footer */}
                <div className="mt-6 flex justify-end">
                  <Button onClick={onClose} variant="secondary">
                    Close
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
